import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import { useLanguage } from '../../contexts/LanguageContext';
import { getQuestionHeading } from '../../seo/contentMetadata';
import ContentDate from '../common/ContentDate';
import SafeIcon from '../common/SafeIcon';
import MokugyoIcon from '../ui/MokugyoIcon';
import TypeIcon from '../ui/TypeIcon';

const HelpModal = ({ onClose }) => {
  const { t, language } = useLanguage();
  const [activeTab, setActiveTab] = useState('gameplay');
  const heading = getQuestionHeading('help.title', language);

  const tabs = [
    { id: 'gameplay', name: t('help.tabs.gameplay'), icon: '🎮' },
    { id: 'types', name: t('help.tabs.types'), icon: '🎭' },
    {
      id: 'fish',
      name: t('help.tabs.fish'),
      icon: <MokugyoIcon type="normal" size={24} />,
    },
    {
      id: 'achievements',
      name: t('help.tabs.achievements'),
      icon: '🏆',
    },
    { id: 'tips', name: t('help.tabs.tips'), icon: '💡' },
  ];

  return (
    <article
      className="help-modal-container"
      aria-labelledby="help-modal-heading"
      itemScope
      itemType="https://schema.org/Article"
    >
      <header className="help-modal-header">
        <div className="help-modal-header-content">
          <h2
            id="help-modal-heading"
            className="help-modal-title"
            itemProp="headline"
          >
            <span aria-hidden="true" style={{ fontSize: '24px' }}>
              🎮
            </span>
            {heading}
          </h2>
          <button
            onClick={onClose}
            className="help-modal-close-btn"
            aria-label={t('common.close')}
          >
            <SafeIcon icon={FiX} className="text-xl" />
          </button>
        </div>

        <ContentDate />

        <nav
          className="help-modal-tabs-grid"
          data-lang={language}
          aria-label={heading}
        >
          {tabs.map((tab) => (
            <motion.button
              key={tab.id}
              className={`help-modal-tab-grid ${
                activeTab === tab.id ? 'active' : ''
              }`}
              onClick={() => setActiveTab(tab.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              aria-pressed={activeTab === tab.id}
            >
              <span className="help-modal-tab-icon flex items-center justify-center">
                {tab.icon}
              </span>
              <span className="help-modal-tab-text">{tab.name}</span>
            </motion.button>
          ))}
        </nav>
      </header>

      <div className="help-modal-content-expanded" itemProp="articleBody">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {activeTab === 'gameplay' && <GameplayTab />}
            {activeTab === 'types' && <TypesTab />}
            {activeTab === 'fish' && <FishTab />}
            {activeTab === 'achievements' && <AchievementsTab />}
            {activeTab === 'tips' && <TipsTab />}
          </motion.div>
        </AnimatePresence>
      </div>
    </article>
  );
};

const GameplayTab = () => {
  const { t } = useLanguage();
  const steps = [
    {
      icon: '👆',
      title: t('help.gameplay.step1.title'),
      description: t('help.gameplay.step1.description'),
    },
    {
      icon: '⚡',
      title: t('help.gameplay.step2.title'),
      description: t('help.gameplay.step2.description'),
    },
    {
      icon: '🔥',
      title: t('help.gameplay.step3.title'),
      description: t('help.gameplay.step3.description'),
    },
    {
      icon: '🤖',
      title: t('help.gameplay.step4.title'),
      description: t('help.gameplay.step4.description'),
    },
  ];

  return (
    <section className="help-tab-content">
      {steps.map((step, index) => (
        <motion.div
          key={step.title}
          className="help-card-compact"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.08 }}
        >
          <span className="help-card-icon-compact" aria-hidden="true">
            {step.icon}
          </span>
          <div className="help-card-content-compact">
            <h3 className="help-card-title-compact">{step.title}</h3>
            <p className="help-card-description-compact">
              {step.description}
            </p>
          </div>
        </motion.div>
      ))}

      <div className="help-controls-card-compact">
        <h3 className="help-controls-title-compact">
          <span aria-hidden="true" style={{ fontSize: '20px' }}>
            🎮
          </span>{' '}
          {t('help.controls.title')}
        </h3>
        <div className="help-controls-grid">
          <Control icon="🖱️" label={t('help.controls.mouse')} />
          <Control icon="📱" label={t('help.controls.touch')} />
          <Control icon="⌨️" label={t('help.controls.keyboard')} />
        </div>
      </div>
    </section>
  );
};

const Control = ({ icon, label }) => (
  <div className="help-control-item-compact">
    <span className="help-control-icon-compact" aria-hidden="true">
      {icon}
    </span>
    <span className="help-control-text-compact">{label}</span>
  </div>
);

const TypesTab = () => {
  const { t } = useLanguage();
  const types = [
    {
      id: 'merit',
      color: 'from-amber-400 to-orange-500',
      particles: ['🙏', '✨', '⭐'],
    },
    {
      id: 'love',
      color: 'from-pink-400 to-rose-500',
      particles: ['💖', '💕', '💗', '❤️'],
    },
    {
      id: 'wealth',
      color: 'from-yellow-400 to-amber-500',
      particles: ['💰', '💵', '💴', '🪙'],
    },
    {
      id: 'health',
      color: 'from-green-400 to-emerald-500',
      particles: ['🍃', '🌿', '💚', '✨'],
    },
  ];

  return (
    <section className="help-tab-content">
      {types.map((type, index) => (
        <motion.div
          key={type.id}
          className="help-type-card-compact"
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.08 }}
        >
          <div
            className={`help-type-bg bg-gradient-to-br ${type.color}`}
          />
          <div className="help-type-content-compact">
            <span className="help-type-icon-compact">
              <TypeIcon type={type.id} size={40} />
            </span>
            <div className="help-type-info-compact">
              <h3 className="help-type-title-compact">
                {t(`types.${type.id}.name`)}
              </h3>
              <p className="help-type-description-compact">
                {t(`types.${type.id}.description`)}
              </p>
            </div>
            <div
              className="help-type-particles-compact"
              aria-hidden="true"
            >
              {type.particles.map((particle, particleIndex) => (
                <motion.span
                  key={`${type.id}-${particleIndex}`}
                  className="help-type-particle-compact"
                  animate={{ y: [0, -6, 0], rotate: [0, 6, -6, 0] }}
                  transition={{
                    duration: 1.8,
                    repeat: Infinity,
                    delay: particleIndex * 0.15,
                  }}
                >
                  {particle}
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>
      ))}
    </section>
  );
};

const FishTab = () => {
  const { t } = useLanguage();
  const fishList = [
    { id: 'normal', level: 'common' },
    { id: 'golden', level: 'rare' },
    { id: 'crystal', level: 'epic' },
    { id: 'jade', level: 'rare' },
    { id: 'ruby', level: 'rare' },
    { id: 'cat', level: 'rare' },
    { id: 'rainbow', level: 'epic' },
    { id: 'cosmic', level: 'legendary' },
  ];

  const levelColors = {
    common: 'bg-gray-100 text-gray-700',
    rare: 'bg-blue-100 text-blue-700',
    epic: 'bg-purple-100 text-purple-700',
    legendary: 'bg-amber-100 text-amber-700',
  };

  return (
    <section className="help-tab-content">
      <div className="help-fish-info-compact">
        <span className="help-info-icon" aria-hidden="true">
          ℹ️
        </span>
        <p className="help-fish-info-text-compact">
          {t('help.fish.description')}
        </p>
      </div>

      <div className="help-fish-grid-compact">
        {fishList.map((fish, index) => (
          <motion.div
            key={fish.id}
            className="help-fish-card-compact"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.04 }}
            whileHover={{ scale: 1.03 }}
          >
            <div className="help-fish-emoji-compact">
              <MokugyoIcon type={fish.id} size={48} />
            </div>
            <h3 className="help-fish-name-compact">
              {t(`specialFish.types.${fish.id}.name`)}
            </h3>
            <p className="help-fish-description-compact">
              {t(`specialFish.types.${fish.id}.description`)}
            </p>
            <div className="help-fish-unlock-compact">
              🔓 {t(`specialFish.types.${fish.id}.unlock`)}
            </div>
            <span
              className={`help-fish-level-compact ${
                levelColors[fish.level]
              }`}
            >
              {t(`help.fish.level.${fish.level}`)}
            </span>
          </motion.div>
        ))}
      </div>
    </section>
  );
};

const AchievementsTab = () => {
  const { t } = useLanguage();
  const categories = [
    { id: 'tap', icon: '🔔', count: 8 },
    { id: 'combo', icon: '⚡', count: 7 },
    { id: 'speed', icon: '🚀', count: 5 },
    { id: 'type', icon: '🎭', count: 15 },
    { id: 'auto', icon: '🤖', count: 6 },
    { id: 'special', icon: '✨', count: 17 },
  ];
  const rarities = [
    'common',
    'uncommon',
    'rare',
    'epic',
    'legendary',
    'mythic',
  ];

  return (
    <section className="help-tab-content">
      <div className="help-achievement-info-compact">
        <span className="help-info-icon" aria-hidden="true">
          🎯
        </span>
        <p className="help-achievement-info-text-compact">
          {t('help.achievements.description')}
        </p>
      </div>

      <div className="help-achievement-grid-compact">
        {categories.map((category, index) => (
          <motion.div
            key={category.id}
            className="help-achievement-card-compact"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.04 }}
          >
            <div
              className="help-achievement-icon-compact"
              aria-hidden="true"
            >
              {category.icon}
            </div>
            <h3 className="help-achievement-name-compact">
              {t(`achievements.categories.${category.id}`)}
            </h3>
            <div className="help-achievement-count-compact">
              {category.count} {t('help.achievements.achievements')}
            </div>
          </motion.div>
        ))}
      </div>

      <div className="help-rarity-card-compact">
        <h3 className="help-rarity-title-compact">
          <span aria-hidden="true" style={{ fontSize: '20px' }}>
            💎
          </span>{' '}
          {t('help.achievements.rarity')}
        </h3>
        <div className="help-rarity-grid">
          {rarities.map((rarity, rarityIndex) => (
            <div key={rarity} className="help-rarity-item-compact">
              <span className="help-rarity-name-compact">
                {t(`achievements.rarity.${rarity}`)}
              </span>
              <div
                className="help-rarity-stars-compact"
                aria-hidden="true"
              >
                {Array.from({ length: rarityIndex + 1 }).map(
                  (_, starIndex) => (
                    <span
                      key={starIndex}
                      className="help-rarity-star"
                    >
                      ⭐
                    </span>
                  ),
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const TipsTab = () => {
  const { t } = useLanguage();
  const tips = [
    { id: 'tip1', icon: '🎯' },
    { id: 'tip2', icon: '⚡' },
    { id: 'tip3', icon: '🤖' },
    { id: 'tip4', icon: '🏆' },
    { id: 'tip6', icon: '🎨' },
  ];

  return (
    <section className="help-tab-content">
      {tips.map((tip, index) => (
        <motion.div
          key={tip.id}
          className="help-tip-card-compact"
          initial={{ opacity: 0, x: -15 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.08 }}
        >
          <span className="help-tip-icon-compact" aria-hidden="true">
            {tip.icon}
          </span>
          <div className="help-tip-content-compact">
            <h3 className="help-tip-title-compact">
              {t(`help.tips.${tip.id}.title`)}
            </h3>
            <p className="help-tip-description-compact">
              {t(`help.tips.${tip.id}.description`)}
            </p>
          </div>
        </motion.div>
      ))}

      <div className="help-shortcuts-card-compact">
        <h3 className="help-shortcuts-title-compact">
          <span aria-hidden="true" style={{ fontSize: '20px' }}>
            ⌨️
          </span>{' '}
          {t('help.shortcuts.title')}
        </h3>
        <div className="help-shortcuts-grid">
          <Shortcut label={t('help.shortcuts.space')} keyName="Space" />
          <Shortcut label={t('help.shortcuts.esc')} keyName="Esc" />
        </div>
      </div>
    </section>
  );
};

const Shortcut = ({ label, keyName }) => (
  <div className="help-shortcut-item-compact">
    <span className="help-shortcut-label-compact">{label}</span>
    <kbd className="help-shortcut-key-compact">{keyName}</kbd>
  </div>
);

HelpModal.managesContentDate = true;

export default HelpModal;